import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import App from './App';
import { Auth0Provider } from '@auth0/auth0-react';
import './index.css';
// Luxury Design System
import './design-system/luxury-tokens.css';
import './design-system/luxury-base.css';
import './design-system/luxury-components.css';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

const isPrerendering = navigator.userAgent === 'ReactSnap';

const domain = process.env.REACT_APP_AUTH0_DOMAIN;
const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;

// During prerendering, skip Auth0 and service worker — just render the app shell
if (isPrerendering) {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <App />
      </LocalizationProvider>
    </BrowserRouter>
  );
} else if (!domain || !clientId) {
  // Validate required environment variables
  console.error('Missing required Auth0 environment variables. Please check your .env file.');
  console.error('Domain:', domain);
  console.error('Client ID:', clientId);

  // Render error page instead of crashing
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h1>Configuration Error</h1>
      <p>Missing required environment variables:</p>
      <ul style={{ textAlign: 'left', display: 'inline-block' }}>
        <li>REACT_APP_AUTH0_DOMAIN: {domain ? '✅ Set' : '❌ Missing'}</li>
        <li>REACT_APP_AUTH0_CLIENT_ID: {clientId ? '✅ Set' : '❌ Missing'}</li>
      </ul>
      <p>Please check your production environment configuration.</p>
    </div>
  );
} else {
  // Optional: handle where to send users after Auth0 login
  const onRedirectCallback = (appState) => {
    window.history.replaceState(
      {},
      document.title,
      appState?.returnTo || '/family-continuity-structure'
    );
  };

  const appTree = (
    <React.StrictMode>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Auth0Provider
            domain={domain}
            clientId={clientId}
            authorizationParams={{
              redirect_uri: window.location.origin + '/auth/callback',
              audience: "https://api.eternavaults.com"
            }}
            onRedirectCallback={onRedirectCallback}
          >
            <App />
          </Auth0Provider>
        </LocalizationProvider>
      </BrowserRouter>
    </React.StrictMode>
  );

  const rootElement = document.getElementById('root');

  // If pre-rendered HTML exists, hydrate for smooth takeover; otherwise create fresh
  if (rootElement.hasChildNodes()) {
    ReactDOM.hydrateRoot(rootElement, appTree);
  } else {
    const root = ReactDOM.createRoot(rootElement);
    root.render(appTree);
  }

  serviceWorkerRegistration.unregister();
  reportWebVitals();
}
